import { z } from 'zod';
import {
  MAX_MARKET_SYMBOLS,
  MAX_NEWS_COUNT,
  MIN_NEWS_QUERY_CHARS,
  MAX_NEWS_QUERY_CHARS,
  NEWS_QUERY_PATTERN,
  MAX_CALENDAR_EVENTS,
  MAX_CALENDAR_LOOKAHEAD_HOURS,
  MAX_NOTE_CHARS,
  MAX_TODO_ITEM_CHARS,
  MAX_TODO_ITEMS,
  MAX_COUNTDOWN_LABEL_CHARS,
  MAX_RSS_ITEMS,
  SYMBOL_PATTERN,
} from './constants.js';
import { userSourceUrlSchema } from './user-source-url.js';

/**
 * Per-tile-type `source.params` schemas (ADR-002 §3.2).
 *
 * SECURITY: every schema is `.strict()` — unknown keys are rejected, which is
 * what stops a URL/host/path/header being smuggled into params (ADR-002 §7 #1)
 * and blocks prototype-pollution keys (#5). Every list is capped (#4). These are
 * the authoritative param definitions; the human table in ADR-002 §3.2 mirrors them.
 *
 * Params are the raw material of the cache key (ADR-002 §5), so they must contain
 * only canonicalizable scalars and bounded lists — no free-form objects.
 */

/** Known news topics — a fixed menu (also the cost cap: fixed topics => bounded fetches). */
export const NEWS_TOPICS = [
  'world',
  'nation',
  'business',
  'technology',
  'entertainment',
  'sports',
  'science',
  'health',
] as const;

/** Supported leagues for the sports tile (delayed scores via TheSportsDB, D-010). */
export const SPORTS_LEAGUES = [
  'epl',
  'nfl',
  'nba',
  'mlb',
  'nhl',
] as const;

export const weatherParamsSchema = z
  .object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    units: z.enum(['metric', 'imperial']),
  })
  .strict();

export const marketsParamsSchema = z
  .object({
    symbols: z
      .array(z.string().regex(SYMBOL_PATTERN))
      .min(1)
      .max(MAX_MARKET_SYMBOLS),
    display: z.enum(['price', 'change']),
  })
  .strict();

/**
 * news.headlines params. `topic` stays REQUIRED (the base menu). `query` is an
 * OPTIONAL keyword search added at rung-1 (D-017 ladder step 1; ADR-005 §4).
 *
 * SEMANTICS (documented for the news adapter agent): `topic` is always present and
 * is the fallback category; when `query` is present it NARROWS within the topic on
 * providers that support a topic+search combination, and OVERRIDES the topic on
 * providers whose search endpoint is mutually exclusive with the topic feed (GNews:
 * the search endpoint replaces the top-headlines/topic endpoint — the adapter
 * switches endpoints when `query` is set, using `topic` only as the display label
 * and the no-query fallback). Either way `topic` remains a valid, required field so
 * every stored config degrades to a category feed if `query` is later cleared.
 *
 * SECURITY: `query` is constrained by NEWS_QUERY_PATTERN — letters/digits/spaces and
 * a small punctuation set only, NO quotes/backslashes/operators — to keep provider
 * query-injection off the table (ADR-005 §4). Length-bounded 2..60. `.strict()` still
 * rejects any other key. `query` is NOT private content (it is a public search term
 * like `topic`), so unlike note/todo/rss it needs no clone-a-board redaction.
 */
export const newsParamsSchema = z
  .object({
    topic: z.enum(NEWS_TOPICS),
    count: z.number().int().min(1).max(MAX_NEWS_COUNT),
    query: z
      .string()
      .min(MIN_NEWS_QUERY_CHARS)
      .max(MAX_NEWS_QUERY_CHARS)
      .regex(NEWS_QUERY_PATTERN)
      .optional(),
  })
  .strict();

export const sportsParamsSchema = z
  .object({
    league: z.enum(SPORTS_LEAGUES),
    teamId: z.string().regex(/^[a-zA-Z0-9_-]{1,32}$/),
  })
  .strict();

/**
 * Clock has no upstream fetch; params carry only display prefs. `tz` is validated
 * against the platform's IANA set at runtime by the adapter (kept out of the schema
 * to avoid shipping a tz table), but is length-bounded and pattern-checked here so
 * nothing hostile passes the boundary.
 */
export const clockParamsSchema = z
  .object({
    tz: z.string().min(1).max(64).regex(/^[A-Za-z0-9_+\-/]+$/),
    format: z.enum(['12h', '24h']),
  })
  .strict();

/**
 * calendar.peek params (ADR-004 — the client-direct personal tile).
 *
 * These are the ONLY fields a calendar config carries. There is NO url/host/token
 * here — the Google API base lives in the client calendar module and the user's
 * OAuth token lives in the browser (ADR-004), never in a config. `.strict()` keeps
 * a URL/host/token from being smuggled in (ADR-002 §7 #1), exactly as for every
 * other type. The config is PII-free and safe to serialize/share.
 *
 * `calendarId` is `primary`-only in v1.5 (ADR-004): this keeps params canonical and
 * makes the cache-key question moot — a client-direct tile never keys the shared
 * cache, and `primary` is the one calendar every account has. Broadening to a
 * calendar picker (calendar-list ids) is a future, additive change.
 *
 * Both bounds are capped (ADR-002 §7 #4): `maxEvents` and `lookaheadHours` bound
 * the client's own Google query so a hand-edited config cannot request an unbounded
 * event pull.
 */
export const calendarParamsSchema = z
  .object({
    calendarId: z.enum(['primary']),
    maxEvents: z.number().int().min(1).max(MAX_CALENDAR_EVENTS),
    lookaheadHours: z.number().int().min(1).max(MAX_CALENDAR_LOOKAHEAD_HOURS),
  })
  .strict();

/* ==========================================================================
 * Local utility widgets (D-015) — the params ARE the storage.
 *
 * These three types have NO upstream fetch. User content lives entirely in the
 * board config (localStorage), is edited ONLY through configureTile (board-ops),
 * and is computed into display data locally (like clock derives from `Date`).
 * `.strict()` still bites and every field is capped: here the caps are the
 * CONTENT / abuse boundary (how much text can enter a serialized/shared board),
 * not a fan-out limit. Nothing here is ever fetched or transits a server.
 * ========================================================================== */

/**
 * note.sticky params. A single plain-text body. EMPTY IS ALLOWED — a freshly
 * added note is blank and the user types into it (fresh-tile default is `{text:''}`).
 * `.max(MAX_NOTE_CHARS)` is the size boundary for the serialized board blob.
 * Rendered as text, never HTML (ADR-002 §7 #2, generalized to content).
 *
 * PRIVACY: unlike every other param schema, `text` is PRIVATE USER CONTENT
 * (thoughts, reminders, anything). Clone-a-board / any board-sharing feature
 * MUST redact or explicitly consent note bodies before serializing a shared
 * board — do NOT generalize "configs are safe to share" to this type.
 */
export const noteParamsSchema = z
  .object({
    text: z.string().max(MAX_NOTE_CHARS),
  })
  .strict();

/**
 * A single to-do row. `.strict()` so no extra keys ride along in a hand-edited or
 * cloned config. `text` must be NON-EMPTY (a blank row is not a task) and capped;
 * `done` is a plain boolean. Item ORDER is meaningful (see todoParamsSchema).
 */
export const todoItemSchema = z
  .object({
    text: z.string().min(1).max(MAX_TODO_ITEM_CHARS),
    done: z.boolean(),
  })
  .strict();

/**
 * todo.list params: an ORDERED list of items, capped at MAX_TODO_ITEMS. The empty
 * list is allowed (fresh-tile default is `{items:[]}`).
 *
 * ORDERING IS CONTENT: the array order is the user's prioritization and is part of
 * the tile's identity — reordering items IS a param change (unlike the markets
 * `symbols` basket, which is a set). The cache-key canonicalizer (cache-key.ts)
 * treats object arrays as ordered, duplicate-preserving sequences precisely so this
 * holds: reordering or duplicating rows yields a DIFFERENT board-identity key.
 *
 * PRIVACY: item text is PRIVATE USER CONTENT — same clone-a-board redact/consent
 * requirement as noteParamsSchema above.
 */
export const todoParamsSchema = z
  .object({
    items: z.array(todoItemSchema).max(MAX_TODO_ITEMS),
  })
  .strict();

/**
 * An ISO-8601 calendar datetime, refined to reject garbage while ACCEPTING both the
 * `Z` (UTC) form and an explicit numeric offset (e.g. `2026-08-01T09:00:00+05:00`) —
 * the shapes a browser's `Date.toISOString()` / a date-time picker produce. A bare
 * date with no time is rejected (a countdown targets an instant). We validate the
 * SHAPE structurally, then confirm it is a real calendar instant via `Date.parse`
 * (rejects e.g. month 13 / day 32, which the regex alone would let through).
 *
 * PAST DATES ARE ALLOWED (see countdownParamsSchema): no lower bound on the instant.
 */
const ISO_DATETIME_SHAPE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

export const isoDateTimeSchema = z.string().refine(
  (s) => ISO_DATETIME_SHAPE.test(s) && !Number.isNaN(Date.parse(s)),
  { message: 'targetISO must be a valid ISO-8601 datetime (with Z or a ±hh:mm offset)' },
);

/**
 * countdown.event params: a labelled target instant.
 *   - `label`   — non-empty, capped plain text ("Launch day"); rendered as text.
 *   - `targetISO` — a valid ISO-8601 datetime (isoDateTimeSchema).
 *
 * PAST TARGETS ARE ALLOWED BY DESIGN: after the instant passes, the widget shows an
 * ELAPSED / "since" state (daysLeft/hoursLeft go negative, sinceOrUntil → 'since').
 * So there is deliberately no "future-only" refinement here — a past instant is a
 * legitimate, fully-functional countdown (a "days since" tracker).
 */
export const countdownParamsSchema = z
  .object({
    label: z.string().min(1).max(MAX_COUNTDOWN_LABEL_CHARS),
    targetISO: isoDateTimeSchema,
  })
  .strict();

/* ==========================================================================
 * User-source tiles (D-017 / ADR-005) — the FOURTH tile class.
 *
 * A user-supplied https URL, fetched CLIENT-SIDE ONLY (the user's own browser,
 * NEVER our Lambda — same clientDirect discipline as calendar.peek, ADR-004).
 * The URL is the ONE amendment to ADR-002's "no URLs in configs" rule, scoped to
 * `userSourceUrlSchema` and its constraints (https-only, no credentials, no port,
 * private-address hygiene, length cap). Content is rendered TEXT-ONLY through the
 * existing glance/detail chrome (React-escaped). CORS-blocked feeds degrade
 * honestly ("this feed doesn't allow direct access"). See user-source-url.ts.
 * ========================================================================== */

/**
 * rss.feed params: a user-supplied feed URL + a capped item count.
 *   - `feedUrl`  — a constrained https URL (userSourceUrlSchema). This is the one
 *                  place a config carries a URL (ADR-005 §2 amends ADR-002 §2.1).
 *   - `maxItems` — how many feed items to surface (1..MAX_RSS_ITEMS). Bounds the
 *                  client's own render list; never an infinite feed (ADR-005 §3).
 * `.strict()` rejects any other key.
 *
 * PRIVACY (mirror note.sticky / todo.list): `feedUrl` is PRIVATE USER PARAM. A
 * cloned URL tile is a TRACKING BEACON — importing it would make the recipient's
 * browser hit a URL the author chose (and can log/fingerprint). Clone-a-board / any
 * board-sharing feature MUST redact or explicitly consent `feedUrl` before
 * serializing a shared board — do NOT generalize "configs are safe to share" to
 * this type (ADR-005 §"Privacy"). The URL is validated + React-escaped on render;
 * it is never executed, only fetched by the user's own browser.
 */
export const rssParamsSchema = z
  .object({
    feedUrl: userSourceUrlSchema,
    maxItems: z.number().int().min(1).max(MAX_RSS_ITEMS),
  })
  .strict();

/**
 * Re-export the user-source URL schema from its dedicated module so consumers can
 * import it from `./params` alongside the other param schemas (it is the value type
 * of rss.feed's `feedUrl`). The security logic lives in user-source-url.ts.
 */
export { userSourceUrlSchema, isSafeUserSourceUrl } from './user-source-url.js';
export type { UserSourceUrl } from './user-source-url.js';

export type WeatherParams = z.infer<typeof weatherParamsSchema>;
export type MarketsParams = z.infer<typeof marketsParamsSchema>;
export type NewsParams = z.infer<typeof newsParamsSchema>;
export type SportsParams = z.infer<typeof sportsParamsSchema>;
export type ClockParams = z.infer<typeof clockParamsSchema>;
export type CalendarParams = z.infer<typeof calendarParamsSchema>;
export type NoteParams = z.infer<typeof noteParamsSchema>;
export type TodoItem = z.infer<typeof todoItemSchema>;
export type TodoParams = z.infer<typeof todoParamsSchema>;
export type CountdownParams = z.infer<typeof countdownParamsSchema>;
export type RssParams = z.infer<typeof rssParamsSchema>;
